import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import Navbar from "@/components/Navbar";

const functionalAreas = [
  {
    title: "Project Management & Scheduling",
    description: "Streamline design-to-occupancy timelines through better project coordination",
    isRelevant: true
  },
  {
    title: "Construction & Renovation",
    description: "Optimize renovation processes and reduce construction delays",
    isRelevant: true
  },
  {
    title: "Design & Planning",
    description: "Accelerate design approval and planning phases",
    isRelevant: true
  },
  {
    title: "Regulatory Compliance",
    description: "Speed up permitting and compliance review processes",
    isRelevant: true
  },
  {
    title: "Procurement & Contracting",
    description: "Streamline contractor selection and procurement workflows",
    isRelevant: false
  },
  {
    title: "Technology Integration",
    description: "Leverage digital tools for faster project delivery",
    isRelevant: false
  }
];

const GsaChallenge1FunctionalArea = () => {
  const navigate = useNavigate();
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    functionalAreas.filter(area => area.isRelevant).map(area => area.title)
  );

  const handleAreaToggle = (areaTitle: string) => {
    setSelectedAreas(prev => 
      prev.includes(areaTitle) 
        ? prev.filter(title => title !== areaTitle)
        : [...prev, areaTitle]
    );
  };

  const handleContinue = () => {
    navigate("/gsa-challenge-1/bounty", {
      state: { 
        selectedAreas,
        challenge: "How can we reduce the average design-to-occupancy timeline for federal office renovations by 20%?"
      }
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">GSA Federal Office Renovation Timeline</h1>
          <p className="text-lg text-gray-600 mb-8">
            Select the functional areas most relevant to reducing design-to-occupancy timelines
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-xl text-blue-800">Challenge Statement</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-900 font-medium">
              How can we reduce the average design-to-occupancy timeline for federal office renovations by 20%?
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {functionalAreas.map((area) => (
            <Card 
              key={area.title}
              className={`cursor-pointer transition-all duration-200 ${
                selectedAreas.includes(area.title)
                  ? 'border-2 border-blue-500 bg-blue-50'
                  : 'border border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => handleAreaToggle(area.title)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    checked={selectedAreas.includes(area.title)}
                    onChange={() => handleAreaToggle(area.title)}
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{area.title}</h3>
                    <p className="text-sm text-gray-600">{area.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-call-for-solutions")}
          >
            Back to Challenges
          </Button>
          
          <Button 
            onClick={handleContinue}
            disabled={selectedAreas.length === 0}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white"
          >
            Continue to Bounty Setup
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaChallenge1FunctionalArea;